import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, createdSecrets, type CreatedStream, type Stream } from '../api';
import { useMe } from '../queries';

type Retention = 'default' | 'days' | 'forever';

export function NewStream() {
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const trusted = me.data?.account.trust_tier === 'trusted' || me.data?.account.trust_tier === 'partner';
  const accountDefault = me.data?.account.recording_retention_days;
  const [name, setName] = useState('');
  const [resolution, setResolution] = useState(720);
  const [profile, setProfile] = useState<'abr_basic' | 'passthrough'>('abr_basic');
  const [record, setRecord] = useState(true);
  const [replay, setReplay] = useState(false);
  const [showName, setShowName] = useState(true);
  const [members, setMembers] = useState(false);
  const [thumbPublic, setThumbPublic] = useState(false);
  const [retention, setRetention] = useState<Retention>('default');
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per form: a double click or a retry after a network error can't create two streams.
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);

  // "Create again": start from another stream's settings.
  const { from } = useSearch({ from: '/app/streams/new' });
  const source = useQuery({
    queryKey: ['stream', from],
    queryFn: () => api<Stream>(`/v1/streams/${encodeURIComponent(from!)}`),
    enabled: !!from,
    staleTime: Infinity,
  });
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    const s = source.data;
    if (!s || prefilled) return;
    setPrefilled(true);
    setName(s.name ?? (typeof s.metadata?.name === 'string' ? s.metadata.name : ''));
    setResolution(s.max_ingest_resolution);
    setProfile(s.profile);
    setRecord(s.record);
    setReplay(s.replay ?? false);
    setShowName(s.player?.show_name ?? true);
    setMembers(s.player?.require_viewer_token ?? false);
    setThumbPublic(s.thumbnail?.public ?? false);
  }, [source.data, prefilled]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { max_resolution: resolution, profile, record };
      const player: Record<string, unknown> = {};
      if (name.trim()) {
        body.name = name.trim();
        if (!showName) player.show_name = false;
      }
      if (members) player.require_viewer_token = true;
      if (Object.keys(player).length) body.player = player;
      if (thumbPublic) body.thumbnail = { public: true };
      if (record && replay) body.replay = true;
      if (record && retention === 'days') body.recording_retention_days = days;
      if (record && retention === 'forever') body.recording_retention_days = null;
      const s = await api<CreatedStream>('/v1/streams', { method: 'POST', json: body, headers: { 'Idempotency-Key': idempotencyKey } });
      createdSecrets.set(s.id, { stream_key: s.stream_key, whip_url: s.whip_url });
      await qc.invalidateQueries({ queryKey: ['streams'] });
      navigate({ to: '/streams/$id', params: { id: s.id } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the stream.');
      setBusy(false);
    }
  };

  return (
    <section className="page" style={{ maxWidth: 720 }}>
      <div className="crumbs">
        <Link to="/streams">Streams</Link>
        <span aria-hidden="true">/</span>
        <span>New</span>
      </div>
      <div>
        <h1>New stream</h1>
        <p className="sub">
          You get an RTMP address, a browser (WHIP) address and an embed code right away. Start sending within 30 minutes, or the stream expires and you create
          a new one. Nothing is charged until video arrives.
        </p>
        {from && prefilled && <p className="faint" style={{ margin: '6px 0 0', fontSize: 13 }}>Same settings as the stream you came from.</p>}
      </div>
      <form className="card" onSubmit={submit} style={{ gap: 18 }}>
        <label className="field">
          Name
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="Evening Yin · Class 215" autoFocus />
          <span className="hint">Your API code can set it too (name).</span>
        </label>
        {name.trim() && (
          <label className="check" style={{ marginTop: -6 }}>
            <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} />
            Show the name to viewers on the player
          </label>
        )}

        <div className="grid-2">
          <label className="field">
            Highest resolution you will send
            <select className="input" value={resolution} onChange={(e) => setResolution(Number(e.target.value))}>
              <option value={480}>480p</option>
              <option value={720}>720p</option>
              <option value={1080} disabled={!trusted}>
                1080p{trusted ? '' : ' (after your account is trusted)'}
              </option>
            </select>
            <span className="hint">Viewers get this plus smaller qualities. Sending more ends the stream after a 20 s warning.</span>
          </label>
          <label className="field">
            Processing
            <select className="input" value={profile} onChange={(e) => setProfile(e.target.value as typeof profile)}>
              <option value="abr_basic">Adaptive: several qualities</option>
              <option value="passthrough">Passthrough: exactly what you send</option>
            </select>
            <span className="hint">{profile === 'abr_basic' ? '$0.003 per minute of encoding.' : '$0.0005 per minute. Viewers on weak connections may buffer.'}</span>
          </label>
        </div>

        <label className="check">
          <input type="checkbox" checked={members} onChange={(e) => setMembers(e.target.checked)} />
          Members only: the player needs a viewer token from your site (
          <a href="https://docs.justonair.com/viewer-tokens#members-only-streams" target="_blank" rel="noreferrer">
            how
          </a>
          )
        </label>
        <label className="check">
          <input type="checkbox" checked={record} onChange={(e) => setRecord(e.target.checked)} />
          Record
        </label>
        {record && (
          <label className="check" style={{ marginTop: -6 }}>
            <input type="checkbox" checked={replay} onChange={(e) => setReplay(e.target.checked)} />
            Replay: after the end, the player plays the recording (a few minutes after, for as long as it is kept)
          </label>
        )}
        {record && (
          <div className="grid-2">
            <label className="field">
              Keep the recording for
              <select className="input" value={retention} onChange={(e) => setRetention(e.target.value as Retention)}>
                <option value="default">{accountDefault == null ? 'Account default (until deleted)' : `Account default (${accountDefault} days)`}</option>
                <option value="days">A number of days</option>
                <option value="forever">Until I delete it</option>
              </select>
            </label>
            {retention === 'days' && (
              <label className="field">
                Days
                <input className="input" type="number" min={1} max={3650} value={days} onChange={(e) => setDays(Number(e.target.value))} />
              </label>
            )}
          </div>
        )}

        <label className="check">
          <input type="checkbox" checked={thumbPublic} onChange={(e) => setThumbPublic(e.target.checked)} />
          Public thumbnail: show the newest frame as the player's poster and at a public image URL
        </label>

        {error && <p className="error-text" role="alert" style={{ margin: 0 }}>{error}</p>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Link to="/streams" className="btn">
            Cancel
          </Link>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create stream'}
          </button>
        </div>
      </form>
    </section>
  );
}
